/**
 * RichTextPromptInput - ContentEditable mit inline Asset-Badges.
 *
 * Features:
 * - @ öffnet Asset-Dropdown
 * - Inline farbige Badges (wie Freepik)
 * - Click auf Badge → Dropdown zum Wechseln
 * - Backspace/Delete löscht Badge
 */
import React, { useCallback, useState } from 'react';
import { useContentEditableState } from '@/hooks/useContentEditableState';
import { useBrandAssets } from '@/hooks/useBrands';
import { deleteBadgeAtCursor, insertTextAtCursor, type AssetBadgeData } from '@/lib/utils/contentEditable';
import { AssetBadgeDropdown, type BrandAssetOption } from './AssetBadgeDropdown';
import './RichTextPromptInput.css';

interface RefSlot { id: string | null; previewUrl: string | null; }

interface RichTextPromptInputProps {
  brandId: string | null;
  value: string;
  onChange: (text: string, assetIds: string[]) => void;
  placeholder?: string;
  className?: string;
  referenceSlots?: RefSlot[];
}

export const RichTextPromptInput: React.FC<RichTextPromptInputProps> = ({
  brandId,
  onChange,
  placeholder = 'Beschreibe dein gewünschtes Bild...',
  className = '',
  referenceSlots = [],
}) => {
  const { data: brandAssets } = useBrandAssets(brandId || '');
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

  const {
    editorRef,
    insertBadge,
    replaceBadge,
    triggerChange,
    dropdownOpen,
    setDropdownOpen,
    dropdownPosition,
    selectedBadgeId,
  } = useContentEditableState({ onChange });

  // Available assets für Dropdown
  const availableAssets: BrandAssetOption[] = React.useMemo(() => {
    const assets: BrandAssetOption[] = [];

    if (brandAssets) {
      // Logo slots
      brandAssets.logo_slots?.forEach((slot) => {
        if (slot.asset_id && slot.asset_url) {
          assets.push({
            assetId: slot.asset_id,
            assetName: slot.asset_filename || `Logo ${slot.slot}`,
            assetType: 'logo',
            thumbnailUrl: slot.asset_url.replace('/file', '/thumb'),
          });
        }
      });

      // Style slots
      brandAssets.style_image_slots?.forEach((slot) => {
        if (slot.asset_id && slot.asset_url) {
          assets.push({
            assetId: slot.asset_id,
            assetName: slot.asset_filename || `Style ${slot.slot}`,
            assetType: 'style_image',
            thumbnailUrl: slot.asset_url.replace('/file', '/thumb'),
          });
        }
      });
    }

    // REF images (nur hochgeladene Slots)
    referenceSlots.forEach((slot, i) => {
      if (slot.id && slot.previewUrl) {
        assets.push({
          assetId: `ref:${i}`,
          assetName: `REF ${i + 1}`,
          assetType: 'reference_image',
          thumbnailUrl: slot.previewUrl,
        });
      }
    });

    return assets;
  }, [brandAssets, referenceSlots]);

  // @ Detection via Input (works with AltGr + Q on German keyboards)
  const handleInput = useCallback(
    (_e: React.FormEvent<HTMLDivElement>) => {
      triggerChange();

      // Check if @ was just typed
      if (availableAssets.length === 0 || !editorRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Get text before cursor
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      const textBeforeCursor = preCaretRange.toString();

      // If @ was just typed (last character before cursor)
      if (textBeforeCursor.endsWith('@')) {
        const rect = range.getBoundingClientRect();

        setPickerPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
        });
        setShowAssetPicker(true);
      }
    },
    [availableAssets, editorRef, triggerChange]
  );

  // Backspace/Delete Badge
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Backspace' || e.key === 'Delete') && editorRef.current) {
        const deleted = deleteBadgeAtCursor(editorRef.current);
        if (deleted) {
          e.preventDefault();
          triggerChange();
        }
      }
    },
    [editorRef, triggerChange]
  );

  // Asset Selection (from @ picker)
  const handleAssetSelect = useCallback(
    (asset: BrandAssetOption) => {
      if (asset.assetType === 'reference_image' && editorRef.current) {
        // REF-Bilder als Plain-Text einfügen (z.B. @REF_1)
        const idx = parseInt(asset.assetId.replace('ref:', ''), 10);
        insertTextAtCursor(editorRef.current, `@REF_${idx + 1}`);
        triggerChange();
      } else {
        const badgeData: AssetBadgeData = {
          assetId: asset.assetId,
          assetName: asset.assetName,
          assetType: asset.assetType as 'logo' | 'style_image',
          thumbnailUrl: asset.thumbnailUrl,
        };
        insertBadge(badgeData);
      }
      setShowAssetPicker(false);
    },
    [insertBadge, editorRef, triggerChange]
  );

  // Asset Replace (from badge dropdown)
  const handleAssetReplace = useCallback(
    (newAsset: BrandAssetOption) => {
      if (!selectedBadgeId) return;

      const newBadgeData: AssetBadgeData = {
        assetId: newAsset.assetId,
        assetName: newAsset.assetName,
        assetType: newAsset.assetType,
        thumbnailUrl: newAsset.thumbnailUrl,
      };

      replaceBadge(selectedBadgeId, newBadgeData);
    },
    [selectedBadgeId, replaceBadge]
  );

  return (
    <>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={`relative min-h-[120px] p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${className}`}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* @ Asset Picker */}
      {showAssetPicker && (
        <div
          className="fixed z-50"
          style={{ top: pickerPosition.top, left: pickerPosition.left }}
        >
          <AssetBadgeDropdown
            open={showAssetPicker}
            onOpenChange={setShowAssetPicker}
            availableAssets={availableAssets}
            currentAssetId=""
            onAssetSelect={handleAssetSelect}
          />
        </div>
      )}

      {/* Badge Edit Dropdown */}
      {dropdownOpen && (
        <div
          className="fixed z-50"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <AssetBadgeDropdown
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            availableAssets={availableAssets}
            currentAssetId={selectedBadgeId || ''}
            onAssetSelect={handleAssetReplace}
          />
        </div>
      )}
    </>
  );
};
